import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiSend, FiUser } from 'react-icons/fi';
import { useToast } from '../components/Toast';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

function MessagesView({ userId, userType }) {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [receiverId, setReceiverId] = useState(null);
  const [selectedContactName, setSelectedContactName] = useState('');
  const messagesEndRef = useRef(null);

  // Fetch contacts from bookings
  useEffect(() => {
    fetchContacts();
  }, [userId]);

  useEffect(() => {
    if (receiverId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [userId, receiverId, selectedBooking]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchContacts = async () => {
    try {
      // Fetch bookings to get list of connected lawyers/clients
      const response = await axios.get(`${API_BASE}/bookings/?user_id=${userId}`);
      if (response.data.success && response.data.data) {
        const bookings = response.data.data;
        const contactMap = new Map();

        bookings.forEach(booking => {
          // For clients: show lawyer contacts. For lawyers: show client contacts
          const contactId = userType === 'lawyer' ? booking.client_id : booking.lawyer_id;
          const contactName = userType === 'lawyer' ? booking.client_name : booking.lawyer_name;

          if (contactId && !contactMap.has(contactId)) {
            contactMap.set(contactId, {
              id: contactId,
              name: contactName || `User #${contactId}`,
              booking_id: booking.id,
              service_type: booking.service_type,
              status: booking.status
            });
          }
        });

        const contactList = Array.from(contactMap.values());
        setContacts(contactList);

        // Auto-select first contact
        if (contactList.length > 0 && !receiverId) {
          setReceiverId(contactList[0].id);
          setSelectedContactName(contactList[0].name);
          setSelectedBooking(contactList[0].booking_id);
        }
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!receiverId) return;
    try {
      const url = selectedBooking
        ? `${API_BASE}/messages/?user_id=${userId}&booking_id=${selectedBooking}`
        : `${API_BASE}/messages/?user_id=${userId}`;

      const response = await axios.get(url);
      if (response.data.success) {
        setMessages(response.data.data);
        setError(null);
      }
    } catch (err) {
      if (!error) setError('Failed to load messages');
      console.error(err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectContact = (contact) => {
    setReceiverId(contact.id);
    setSelectedContactName(contact.name);
    setSelectedBooking(contact.booking_id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !receiverId) return;

    try {
      const response = await axios.post(`${API_BASE}/messages/?user_id=${userId}`, {
        receiver_id: receiverId,
        message_text: newMessage,
        booking_id: selectedBooking
      });

      if (response.data.success) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      toast.error('Failed to send message');
      console.error(err);
    }
  };

  if (loading && contacts.length === 0) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      <span className="ml-3 text-gray-500">Loading messages...</span>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FiMessageSquare className="w-5 h-5 text-accent" />
          Messages
        </h2>
        <p className="text-sm text-gray-500 mt-1">Chat with your {userType === 'lawyer' ? 'clients' : 'lawyer'}</p>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-3">
          {/* Contacts Sidebar */}
          <div className="border-r border-gray-100 md:block">
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">Conversations</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {contacts.length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">
                  <FiUser className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No conversations yet.</p>
                  <p className="text-xs mt-1">Book a consultation to start chatting.</p>
                </div>
              ) : (
                contacts.map(contact => (
                  <div
                    key={contact.id}
                    onClick={() => handleSelectContact(contact)}
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-colors border-b border-gray-50 ${
                      receiverId === contact.id ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FiUser className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${receiverId === contact.id ? 'text-primary' : 'text-gray-800'}`}>
                        {contact.name}
                      </p>
                      <p className="text-xs text-gray-400 truncate capitalize">{contact.service_type || 'Consultation'} • {contact.status}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="md:col-span-2 flex flex-col">
            {/* Chat Header */}
            {receiverId && (
              <div className="p-3 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <FiUser className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-semibold text-gray-800">{selectedContactName}</span>
              </div>
            )}

            {/* Messages List */}
            <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {!receiverId ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <FiMessageSquare className="w-10 h-10 mb-3 opacity-30" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm">Choose a contact to start messaging.</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <FiMessageSquare className="w-10 h-10 mb-3 opacity-30" />
                  <p className="font-medium">No messages yet.</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.is_sent_by_me ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                      msg.is_sent_by_me
                        ? 'bg-primary text-white rounded-br-md'
                        : 'bg-white shadow-sm border border-gray-100 rounded-bl-md'
                    }`}>
                      <p className={`text-xs font-semibold mb-1 ${msg.is_sent_by_me ? 'text-white/70' : 'text-primary'}`}>
                        {msg.sender_name}
                      </p>
                      <p className={`text-sm ${msg.is_sent_by_me ? 'text-white' : 'text-gray-700'}`}>
                        {msg.message_text}
                      </p>
                      <span className={`text-xs mt-1 block ${msg.is_sent_by_me ? 'text-white/50' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Form */}
            {receiverId && (
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100 bg-white">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      rows="2"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors shadow-md"
                  >
                    <FiSend className="w-4 h-4" />
                    Send
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default MessagesView;
